import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
    TerminalCommandReadinessResourceKind,
    TerminalCommandReadinessStatus,
    type TerminalCommandReadinessResultV1,
} from '@pillar-portal/api-types';
import { paymentsInfrastructureApi } from '@/lib/pillarApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export interface TerminalCommandReadinessActionProps {
    readonly resourceKind: TerminalCommandReadinessResultV1['resource']['resource_kind'];
    readonly resourceUuid?: string;
    readonly revisionKey: string;
    readonly unavailableReason?: string;
}

export function TerminalCommandReadinessResultView({
    result,
}: {
    readonly result: TerminalCommandReadinessResultV1;
}): ReactElement {
    return (
        <div className="space-y-2" role="status" aria-live="polite">
            <p className="font-medium">
                {result.resource.resource_kind ===
                    TerminalCommandReadinessResourceKind.TERMINAL_DEVICE &&
                result.resource.is_virtual &&
                result.status === TerminalCommandReadinessStatus.COMMAND_ACCEPTED
                    ? 'Credentials authenticated'
                    : formatTerminalCommandReadinessStatus(result.status)}
            </p>
            <p className="text-sm">{result.message}</p>
            {result.details.ipg_id === 1 &&
            result.resource.resource_kind ===
                TerminalCommandReadinessResourceKind.TERMINAL_DEVICE &&
            !result.resource.is_virtual ? (
                <div className="space-y-1 text-sm text-muted-foreground">
                    <p>
                        Single-MID inventory:{' '}
                        {formatInventoryObservation(result.details.single_mid_inventory)}
                    </p>
                    <p>
                        Multi-MID inventory:{' '}
                        {formatInventoryObservation(result.details.multi_mid_inventory)}
                    </p>
                </div>
            ) : null}
        </div>
    );
}

export function TerminalCommandReadinessAction({
    resourceKind,
    resourceUuid,
    revisionKey,
    unavailableReason,
}: TerminalCommandReadinessActionProps): ReactElement {
    const [result, setResult] = useState<TerminalCommandReadinessResultV1>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const requestRevisionKey = useRef<string | null>(null);
    const mutation = useMutation({
        mutationFn: async (
            request: Required<
                Pick<
                    TerminalCommandReadinessActionProps,
                    'resourceKind' | 'resourceUuid' | 'revisionKey'
                >
            >
        ): Promise<TerminalCommandReadinessResultV1> => {
            switch (request.resourceKind) {
                case TerminalCommandReadinessResourceKind.LOGICAL_TERMINAL:
                    return await paymentsInfrastructureApi.checkLogicalTerminalCommandReadiness(
                        request.resourceUuid
                    );
                case TerminalCommandReadinessResourceKind.TERMINAL_DEVICE:
                    return await paymentsInfrastructureApi.checkTerminalDeviceCommandReadiness(
                        request.resourceUuid
                    );
            }
        },
        onSuccess: (nextResult, request) => {
            if (request.revisionKey !== revisionKey) {
                return;
            }
            setErrorMessage(undefined);
            setResult(nextResult);
        },
        onError: (_error, request) => {
            if (request.revisionKey !== revisionKey) {
                return;
            }
            setResult(undefined);
            setErrorMessage('Unable to test terminal connection. Please retry.');
        },
        onSettled: (_data, _error, request) => {
            if (requestRevisionKey.current === request.revisionKey) {
                requestRevisionKey.current = null;
            }
        },
    });

    useEffect(() => {
        setResult(undefined);
        setErrorMessage(undefined);
        requestRevisionKey.current = null;
    }, [resourceKind, resourceUuid, revisionKey]);

    return (
        <Card data-testid="terminal-command-readiness-action">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Terminal connection</CardTitle>
                <span
                    title={unavailableReason}
                    tabIndex={unavailableReason === undefined ? undefined : 0}
                    aria-label={unavailableReason}
                >
                    <Button
                        type="button"
                        onClick={() => {
                            if (requestRevisionKey.current !== null || resourceUuid === undefined) {
                                return;
                            }
                            requestRevisionKey.current = revisionKey;
                            mutation.mutate({ resourceKind, resourceUuid, revisionKey });
                        }}
                        disabled={
                            mutation.isPending ||
                            unavailableReason !== undefined ||
                            resourceUuid === undefined
                        }
                    >
                        {mutation.isPending ? 'Testing...' : 'Test terminal connection'}
                    </Button>
                </span>
            </CardHeader>
            <CardContent>
                {errorMessage !== undefined ? (
                    <p className="text-destructive" role="alert">
                        {errorMessage}
                    </p>
                ) : result === undefined ? (
                    <p className="text-sm text-muted-foreground">
                        Run the test to verify virtual-terminal credentials or a physical terminal's
                        gateway connection.
                    </p>
                ) : (
                    <TerminalCommandReadinessResultView result={result} />
                )}
            </CardContent>
        </Card>
    );
}

export function formatTerminalCommandReadinessStatus(
    status: TerminalCommandReadinessResultV1['status']
): string {
    switch (status) {
        case TerminalCommandReadinessStatus.COMMAND_ACCEPTED:
            return 'Command accepted';
        case TerminalCommandReadinessStatus.PROVIDER_REPORTED_ONLINE:
            return 'Provider reports online';
        case TerminalCommandReadinessStatus.OFFLINE:
            return 'Terminal offline';
        case TerminalCommandReadinessStatus.UNAVAILABLE:
            return 'Provider unavailable';
        case TerminalCommandReadinessStatus.AUTHENTICATION_REJECTED:
            return 'Authentication rejected';
        case TerminalCommandReadinessStatus.TIMEOUT:
            return 'Request timed out';
        case TerminalCommandReadinessStatus.MALFORMED_RESPONSE:
            return 'Malformed provider response';
        case TerminalCommandReadinessStatus.INVALID_CONFIGURATION:
            return 'Invalid configuration';
        case TerminalCommandReadinessStatus.NOT_APPLICABLE:
            return 'Not applicable';
    }
}

function formatInventoryObservation(
    observation: Extract<TerminalCommandReadinessResultV1['details'], { ipg_id: 1 }>[
        | 'single_mid_inventory'
        | 'multi_mid_inventory']
): string {
    if (!observation.found) {
        return 'not found';
    }
    return observation.online ? 'found online' : 'found offline';
}
